import React, { memo, useState, useEffect, useRef } from 'react';
import { Handle, Position } from 'reactflow';
import JobsTableModal from './JobsTableModal';
import ConstraintsPanel from './ConstraintsPanel';
import { downloadFile } from './utils/downloadUtils';
import { getWebSocketUrl } from './utils/websocketUtils';
import { useGlobalConfig } from '../../contexts/GlobalConfigContext';

// Warnings a GA result carries: jobs the optimizer PARKED (no legal slot under a
// hard rule, so they are NOT in the schedule) and any hard-rule violations it
// reported. Appended to the "schedule ready" notice so neither passes unseen.
const gaWarningsText = (s) => {
  const parts = [];
  if (Array.isArray(s?.parked_jobs) && s.parked_jobs.length) {
    const ids = s.parked_jobs.slice(0, 3).map(p => p.job_id).join(', ');
    parts.push(`⚠ ${s.parked_jobs.length} job(s) PARKED, not scheduled: ${ids}${s.parked_jobs.length > 3 ? ', …' : ''} (see Excel "parked:" rows)`);
  }
  if (Array.isArray(s?.validation_violations) && s.validation_violations.length) {
    parts.push(`❌ ${s.validation_violations.length} hard-constraint violation(s) reported`);
  }
  return parts.length ? ' — ' + parts.join('; ') : '';
};

const MachineNode = ({ id, data, isConnectable }) => {
  const { globalConfig, registerWcStartDate, isWcOptimizeEnabled, setWcOptimize } = useGlobalConfig();
  // Is this work center included when the dispatcher's common optimize button is
  // pressed? Checked unless the user unticked it (see GlobalConfigContext).
  const optimizeEnabled = isWcOptimizeEnabled(data.label);

  // Optimization state
  const [optimizationProgress, setOptimizationProgress] = useState(0);
  const [isOptimizing, setIsOptimizing] = useState(data.isOptimizing || false);
  const [optimizationStartTime, setOptimizationStartTime] = useState(null);
  const [hasOptimized, setHasOptimized] = useState(false);
  const [postProcessingStatus, setPostProcessingStatus] = useState(null); // e.g. 'Finalizing results...'
  const [optimizationError, setOptimizationError] = useState(null);
  const [optimizeTimeLimit, setOptimizeTimeLimit] = useState(5); // In minutes (more user-friendly)
  const [batchSize, setBatchSize] = useState(40);  // Jobs per batch from config
  
  // 🆕 Runtime configuration state
  const [scheduleStartDate, setScheduleStartDate] = useState('');
  const [maxJobs, setMaxJobs] = useState(40);
  const [showAdvancedConfig, setShowAdvancedConfig] = useState(false);
  
  // 🆕 Constraints state
  const [constraints, setConstraints] = useState(null);
  const [showConstraintsPanel, setShowConstraintsPanel] = useState(false);
  
  // 🆕 AUTO-BATCH OPTIMIZATION STATE
  const [isAutoOptimizing, setIsAutoOptimizing] = useState(false);
  const [isPaused, setIsPaused] = useState(false); // Track paused state
  const [pausePending, setPausePending] = useState(false); // Track if pause is requested but batch is finishing
  const pauseOptimizationRef = useRef(false); // ✅ Use ref for pause flag
  const [isGaRun, setIsGaRun] = useState(false); // True while a GA run is active (GA supports Stop, not Pause)
  const gaStopRef = useRef(null); // Stops the GA status-poll loop and resets the run UI
  const currentLoopIdRef = useRef(0); // ✅ Track which loop is currently active
  const [currentBatchNumber, setCurrentBatchNumber] = useState(0);
  const [completedBatches, setCompletedBatches] = useState([]);
  const [inProgressBatch, setInProgressBatch] = useState(null);
  
  // Job data state
  const [actualScheduledJobs, setActualScheduledJobs] = useState(null);
  const [actualOptimizedJobs, setActualOptimizedJobs] = useState(null);
  const [lastOptimizationResults, setLastOptimizationResults] = useState(null);
  const [scheduledJobsDetails, setScheduledJobsDetails] = useState([]);
  
  // UI state
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [isEditingCapabilities, setIsEditingCapabilities] = useState(false);
  const [description, setDescription] = useState(data.description || '');
  const [selectedCapabilities, setSelectedCapabilities] = useState(data.capabilities || ['coating']);
  const [showJobModal, setShowJobModal] = useState(false);
  const [jobModalMode, setJobModalMode] = useState('optimized');
  
  // Global optimize-all lock: disable per-WC optimize while Optimize All is running
  const [isGlobalOptimizing, setIsGlobalOptimizing] = useState(false);
  useEffect(() => {
    const handler = (e) => {
      const { isOptimizingAll, workCenters } = e.detail;
      // Only lock MachineNodes during a true multi-WC run (more than 1 WC).
      // Single-WC optimization is handled by the WC's own isOptimizing state;
      // no MachineNode needs the global lock in that case.
      const isMultiWc = !workCenters || workCenters.length > 1;
      setIsGlobalOptimizing(isOptimizingAll && isMultiWc);
      if (isOptimizingAll && isMultiWc) {
        // Clear stale multi-WC results when a new optimize-all starts
        setMultiWcCsvFile(null);
        setMultiWcJobCount(null);
      } else if (!isOptimizingAll && isMultiWc) {
        // Global optimization ended (success or error) — clear stuck auto-optimizing state
        // Only reset if this WC was part of the run
        setIsAutoOptimizing(false);
        setIsOptimizing(false);
        setCurrentBatchNumber(0);
        setPostProcessingStatus(null);
      }
    };
    window.addEventListener('optimizeAllStateChange', handler);
    return () => window.removeEventListener('optimizeAllStateChange', handler);
  }, []);

  // Listen for multi-WC optimization results to show per-WC CSV download
  const [multiWcCsvFile, setMultiWcCsvFile] = useState(null);
  const [multiWcJobCount, setMultiWcJobCount] = useState(null);
  useEffect(() => {
    const handler = (e) => {
      const { csvFiles, jobsByWC } = e.detail;
      const wcLabel = data.label;
      if (csvFiles && csvFiles[wcLabel]) {
        const csvPath = csvFiles[wcLabel];
        const fileName = csvPath.split('/').pop();
        setMultiWcCsvFile(fileName);
        setMultiWcJobCount(jobsByWC?.[wcLabel] || 0);
      } else {
        setMultiWcCsvFile(null);
        setMultiWcJobCount(null);
      }
      // Reset per-WC optimizing state when results arrive (optimization is done)
      if (isOptimizing || isAutoOptimizing) {
        setIsOptimizing(false);
        setIsAutoOptimizing(false);
        setCurrentBatchNumber(0);
        setPostProcessingStatus(null);
        setHasOptimized(true);
        fetchScheduledJobsDetails();
      }
    };
    window.addEventListener('multiWcResultsReady', handler);
    return () => window.removeEventListener('multiWcResultsReady', handler);
  }, [data.label, isOptimizing, isAutoOptimizing]);

  // Listen for multi-WC errors to show on the affected MachineNode
  useEffect(() => {
    const handler = (e) => {
      const { error, workCenter } = e.detail;
      // Show error on the affected work center (or all if no WC specified)
      if (!workCenter || workCenter === data.label) {
        setOptimizationError(error);
        // Reset optimizing state on error so button is clickable again
        setIsOptimizing(false);
        setIsAutoOptimizing(false);
      }
    };
    window.addEventListener('multiWcError', handler);
    return () => window.removeEventListener('multiWcError', handler);
  }, [data.label]);

  // File download state
  const [fileSaveNotification, setFileSaveNotification] = useState(null);
  const [downloadLinks, setDownloadLinks] = useState(null);
  const [allSavedFiles, setAllSavedFiles] = useState([]); // { name, url, type, isFinal }

  // On mount, surface a previously-completed GA run's CSV (so the link survives a
  // page reload without re-running). Harmless if none exists.
  useEffect(() => {
    if (!data.machineId) return;
    const apiBaseUrl = process.env.REACT_APP_API_URL || '';
    fetch(`${apiBaseUrl}/api/machines/${data.machineId}/ga-status`)
      .then(r => r.json())
      .then(s => {
        if (s && s.status === 'done' && s.csv_filename) {
          const csvUrl = `${apiBaseUrl}${s.download_url || `/api/download/${s.csv_filename}`}`;
          // Restore the Excel alongside the CSV too — see applyDone: this panel
          // must not depend on the dispatcher having caught the window event.
          const excelUrl = s.excel_filename
            ? `${apiBaseUrl}${s.excel_download_url || `/api/download/${s.excel_filename}`}`
            : null;
          setDownloadLinks(prev => prev || {
            csv: csvUrl, csvName: s.csv_filename,
            excel: excelUrl, excelName: s.excel_filename || null,
          });
          setAllSavedFiles(prev => (prev && prev.length > 0) ? prev
            : [{ name: s.csv_filename, url: csvUrl, type: 'csv', isFinal: true },
               ...(excelUrl ? [{ name: s.excel_filename, url: excelUrl, type: 'xlsx', isFinal: true }] : [])]);
          setFileSaveNotification(prev => prev || `GA schedule ready: ${s.csv_filename} (${s.jobs_optimized} jobs)${gaWarningsText(s)}`);
          // Reveal the results section (guarded so an OR-Tools restore wins if present).
          setActualScheduledJobs(prev => (prev != null ? prev : (s.jobs_optimized || 0)));
          setLastOptimizationResults(prev => prev || { jobs_optimized: s.jobs_optimized, batches_completed: 1, method: 'ga' });
          setHasOptimized(true);
          // Re-announce the Excel so the dispatcher panel shows it after a reload too.
          if (s.excel_filename) {
            window.dispatchEvent(new CustomEvent('gaResultReady', { detail: {
              excelName: s.excel_filename,
              excelUrl: `${apiBaseUrl}${s.excel_download_url || `/api/download/${s.excel_filename}`}`,
              wc: s.work_center, jobs: s.jobs_optimized,
            }}));
          }
        }
      })
      .catch(() => { /* ignore */ });
  }, [data.machineId]);

  const descriptionInputRef = useRef(null);
  
  const availableCapabilities = [
    'coating',
    'printing',
    'lamination',
    'embossing',
    'compound',
    'fabrication',
    'cnc_machining',
    'pressing',
    'inspection',
    'packaging',
    'testing'
  ];
  
  const [editableData, setEditableData] = useState({
    label: data.label || 'Machine',
    type: data.type || 'CNC_MILL',
    pendingJobs: data.pendingJobs || 0,
    utilization: data.utilization || 0
  });

  // ============================================================================
  // EFFECTS
  // ============================================================================

  // Listen to global config changes and update local state
  // Per-WC overrides stored in localStorage (keyed by WC label) take precedence
  useEffect(() => {
    console.log(`🌐 Global config changed for ${data.label}:`, globalConfig);
    // Restore per-WC overrides from localStorage, fall back to global values
    const savedTimeLimit = localStorage.getItem(`wc-${data.label}-timeLimit`);
    const savedBatchSize = localStorage.getItem(`wc-${data.label}-batchSize`);
    const savedStartDate = localStorage.getItem(`wc-${data.label}-scheduleStartDate`);

    const tl = savedTimeLimit ? parseInt(savedTimeLimit, 10) : globalConfig.timeLimit;
    const bs = savedBatchSize ? parseInt(savedBatchSize, 10) : globalConfig.batchSize;
    const sd = savedStartDate || globalConfig.scheduleStartDate;

    setOptimizeTimeLimit(tl);
    setBatchSize(bs);
    setMaxJobs(bs);
    setScheduleStartDate(sd);
    console.log(`📅 scheduleStartDate="${sd}", timeLimit=${tl}, batchSize=${bs} (per-WC overrides: tl=${!!savedTimeLimit} bs=${!!savedBatchSize} sd=${!!savedStartDate})`);
  }, [globalConfig, data.label]);

  // Register local scheduleStartDate into global context so "Optimize All" can read it
  useEffect(() => {
    if (data.label && registerWcStartDate) {
      registerWcStartDate(data.label, scheduleStartDate);
    }
  }, [scheduleStartDate, data.label, registerWcStartDate]);

  // NOTE: Optimization config (timeLimit, batchSize, scheduleStartDate) is loaded
  // once by GlobalConfigContext from /api/config/optimization and then synced to
  // MachineNode via the effect above.  We intentionally do NOT re-fetch it here
  // so that user changes survive a data re-import (which assigns new machineIds).

  // Fetch constraints for this work center
  useEffect(() => {
    const fetchConstraints = async () => {
      try {
        const apiBaseUrl = process.env.REACT_APP_API_URL || '';
        const workCenterName = data.label || data.machineId; // Use label (e.g., "90L") not UUID
        const response = await fetch(`${apiBaseUrl}/api/config/constraints/${workCenterName}`);
        
        if (response.ok) {
          const result = await response.json();
          console.log(`🔍 Constraints API response for ${workCenterName}:`, result);
          if (result.success) {
            // Set constraints even if empty object
            setConstraints(result.constraints || {});
            console.log(`⚙️ Loaded constraints for ${workCenterName}:`, result.constraints);
          } else {
            console.warn(`⚠️ API returned success=false for ${workCenterName}`);
            setConstraints({});
          }
        } else {
          console.error(`❌ API returned status ${response.status} for ${workCenterName}`);
          setConstraints({});
        }
      } catch (error) {
        console.error('Error fetching constraints:', error);
        setConstraints({});
      }
    };
    
    fetchConstraints();
  }, [data.label, data.machineId]);

  // Fetch optimization state on mount to restore ongoing optimizations
  useEffect(() => {
    const fetchOptimizationState = async () => {
      try {
        const apiBaseUrl = process.env.REACT_APP_API_URL || '';
        const response = await fetch(`${apiBaseUrl}/api/optimization/status/${data.machineId}`);
        
        if (response.ok) {
          const result = await response.json();
          
          if (result.state?.isOptimizing) {
            console.log(`🔄 Restoring optimization state for machine ${data.machineId}:`, result.state);
            
            // Restore time limit from active optimization (takes precedence over localStorage)
            if (result.state.timeLimit) {
              const timeLimitMinutes = Math.round(result.state.timeLimit / 60); // Backend sends seconds, convert to minutes
              setOptimizeTimeLimit(timeLimitMinutes);
              localStorage.setItem(`wc-${data.label}-timeLimit`, timeLimitMinutes.toString());
              console.log(`⏱️ Restored time limit from Redis: ${result.state.timeLimit}s (${timeLimitMinutes} min)`);
            }
            
            // Restore batch size
            if (result.state.jobCount) {
              setBatchSize(result.state.jobCount);
              console.log(`📦 Restored batch size: ${result.state.jobCount} jobs`);
            }
            
            // Restore algorithm if available
            if (result.state.algorithm) {
              window.machineOptimizationAlgorithm = result.state.algorithm;
              console.log(`🎯 Restored algorithm: ${result.state.algorithm}`);
            }
            
            // Restore optimization state
            setIsOptimizing(true);
            setIsAutoOptimizing(true);
            setOptimizationStartTime(result.state.startTime || Date.now());
            setCurrentBatchNumber(result.state.batchNumber || 0);
            
            // Set progress based on last update
            const elapsed = Date.now() - (result.state.lastUpdate || result.state.startTime || Date.now());
            const timeLimit = result.state.timeLimit || optimizeTimeLimit;
            const estimatedProgress = Math.min((elapsed / (timeLimit * 1000)) * 100, 98);
            setOptimizationProgress(estimatedProgress);
          }
          // Note: If no active optimization, time limit was already restored from localStorage in the config fetch
        }
      } catch (error) {
        console.error('Error fetching optimization state:', error);
      }
    };
    
    fetchOptimizationState();
  }, [data.machineId]);

  // Fetch and restore previous optimization results on mount
  useEffect(() => {
    const fetchPreviousResults = async () => {
      try {
        const apiBaseUrl = process.env.REACT_APP_API_URL || '';
        const url = `${apiBaseUrl}/api/machines/${data.machineId}`;
        console.log(`🔍 [Page Load] Fetching previous results from: ${url}`);
        
        const response = await fetch(url);
        
        if (response.ok) {
          const machineData = await response.json();
          console.log(`🔍 [Page Load] Machine data received:`, machineData);
          
          if (machineData.accumulated_optimization) {
            const accumulated = machineData.accumulated_optimization;
            
            // Check if there are any completed batches
            if (accumulated.batches && accumulated.batches.length > 0) {
              
              // Update job counts
              const totalJobs = accumulated.scheduled_jobs?.length || 0;
              setActualScheduledJobs(totalJobs);
              setActualOptimizedJobs(totalJobs);
              
              // Restore optimization results
              const restoredResults = {
                scheduled: totalJobs,
                jobs_optimized: totalJobs,
                batches_completed: accumulated.batches.length,
                shifts_used: accumulated.total_shifts_used || 0,
                completion_time: accumulated.total_completion_time || 0,
                solve_time: accumulated.total_completion_time || 0,
                time_saved_hours: accumulated.total_time_saved_hours || 0,
                time_saved_minutes: accumulated.total_time_saved_minutes || 0,
                improvement_percentage: accumulated.total_improvement_percentage || 0,
                manual_baseline_hours: accumulated.total_manual_baseline_hours || 0,
                manual_schedule_valid: true,
                timestamp: accumulated.batches[accumulated.batches.length - 1]?.timestamp || new Date().toISOString()
              };
              
              setLastOptimizationResults(restoredResults);
              setHasOptimized(true);
              
              // Also fetch and restore scheduled jobs and download links
              if (accumulated.scheduled_jobs && Array.isArray(accumulated.scheduled_jobs)) {
                setScheduledJobsDetails(accumulated.scheduled_jobs);
              }
              
              // Set download links
              const savedFiles = [];
              
              if (accumulated.final_files) {
                // Final combined files available
                const finalFiles = accumulated.final_files;
                
                setDownloadLinks({
                  txt: finalFiles.txt_filename ? `${apiBaseUrl}/api/download/${finalFiles.txt_filename}` : null,
                  csv: `${apiBaseUrl}/api/download/${finalFiles.csv_filename}`,
                  excel: finalFiles.excel_filename ? `${apiBaseUrl}/api/download/${finalFiles.excel_filename}` : null,
                  txtName: finalFiles.txt_filename || null,
                  csvName: finalFiles.csv_filename,
                  excelName: finalFiles.excel_filename || null,
                  manualTxt: finalFiles.manual_txt_filename ? `${apiBaseUrl}/api/download/${finalFiles.manual_txt_filename}` : null,
                  manualCsv: finalFiles.manual_csv_filename ? `${apiBaseUrl}/api/download/${finalFiles.manual_csv_filename}` : null,
                  manualTxtName: finalFiles.manual_txt_filename,
                  manualCsvName: finalFiles.manual_csv_filename
                });
                
                savedFiles.push(
                  { name: finalFiles.csv_filename, url: `${apiBaseUrl}/api/download/${finalFiles.csv_filename}`, type: 'csv', isFinal: true }
                );
                
                if (finalFiles.excel_filename) {
                  savedFiles.push({ name: finalFiles.excel_filename, url: `${apiBaseUrl}/api/download/${finalFiles.excel_filename}`, type: 'xlsx', isFinal: true });
                }
                
                if (finalFiles.txt_filename) {
                  savedFiles.push({ name: finalFiles.txt_filename, url: `${apiBaseUrl}/api/download/${finalFiles.txt_filename}`, type: 'txt', isFinal: true });
                }
                
                if (finalFiles.manual_csv_filename) {
                  savedFiles.push({ name: finalFiles.manual_csv_filename, url: `${apiBaseUrl}/api/download/${finalFiles.manual_csv_filename}`, type: 'csv', isFinal: false });
                }
                if (finalFiles.manual_txt_filename) {
                  savedFiles.push({ name: finalFiles.manual_txt_filename, url: `${apiBaseUrl}/api/download/${finalFiles.manual_txt_filename}`, type: 'txt', isFinal: false });
                }
                
                setFileSaveNotification(`Final results saved: ${finalFiles.csv_filename}`);
              } else if (accumulated.accumulated_csv_file) {
                // Accumulated CSV file available (optimization in progress or paused)
                const accumulatedCsvFilename = accumulated.accumulated_csv_file.split('/').pop();
                
                setDownloadLinks({
                  csv: `${apiBaseUrl}/api/download/${accumulatedCsvFilename}`,
                  csvName: accumulatedCsvFilename
                });
                
                savedFiles.push({
                  name: accumulatedCsvFilename,
                  url: `${apiBaseUrl}/api/download/${accumulatedCsvFilename}`,
                  type: 'csv',
                  isFinal: false,
                  isAccumulated: true
                });
              }
              
              setAllSavedFiles(savedFiles);
              
            }
          }
        } else {
          console.error(`Failed to fetch machine data: ${response.status}`);
        }
      } catch (error) {
        console.error('❌ [Page Load] Failed to fetch previous optimization results:', error);
      }
    };
    
    fetchPreviousResults();
  }, [data.machineId]);

  // Listen for WebSocket optimization events + HTTP Polling Fallback
  useEffect(() => {
    let ws = null;
    let wsConnected = false;
    let pollingInterval = null;
    let reconnectTimeout = null;
    
    // HTTP Polling function as fallback
    const pollOptimizationStatus = async () => {
      try {
        const apiBaseUrl = process.env.REACT_APP_API_URL || '';
        const response = await fetch(`${apiBaseUrl}/api/optimization/status/${data.machineId}`);
        
        if (response.ok) {
          const status = await response.json();
          
          // Handle optimization state based on status.
          // Key off the REAL flag inside state — a completed run leaves a
          // lingering state record, and treating "record exists" as "running"
          // caused a phantom optimizing state on WebSocket reconnect.
          if (status.state?.isOptimizing) {
            if (!isOptimizing) {
              console.log(`🔄 [Polling] Optimization detected as active`);
              setIsOptimizing(true);
              setIsAutoOptimizing(true);
              setOptimizationStartTime(Date.now());
            }
            
            // Update batch number if changed (state.batchNumber from backend)
            if (status.state.batchNumber !== undefined && status.state.batchNumber !== currentBatchNumber) {
              setCurrentBatchNumber(status.state.batchNumber);
              setOptimizationStartTime(Date.now());
              console.log(`🔄 [Polling] Batch #${status.state.batchNumber} detected`);
            }
          } else if (isOptimizing) {
            // Optimization completed
            console.log(`✅ [Polling] Optimization completed detected`);
            setIsOptimizing(false);
            setIsAutoOptimizing(false);
            setOptimizationProgress(100);
            
            // Fetch updated results
            fetchScheduledJobsDetails();
            
            setTimeout(() => {
              setOptimizationProgress(0);
              setHasOptimized(true);
            }, 1000);
          }
          
          // Update job counts from machine data
          const machineResponse = await fetch(`${apiBaseUrl}/api/machines/${data.machineId}`);
          if (machineResponse.ok) {
            const machineData = await machineResponse.json();
            if (machineData.accumulated_optimization) {
              const totalJobs = machineData.accumulated_optimization.scheduled_jobs?.length || 0;
              if (totalJobs > 0 && totalJobs !== actualScheduledJobs) {
                console.log(`📊 [Polling] Updated job count: ${totalJobs}`);
                setActualScheduledJobs(totalJobs);
                setActualOptimizedJobs(totalJobs);
                
                // Update results display
                const accumulated = machineData.accumulated_optimization;
                if (accumulated.batches && accumulated.batches.length > 0) {
                  const results = {
                    scheduled: totalJobs,
                    jobs_optimized: totalJobs,
                    batches_completed: accumulated.batches.length,
                    shifts_used: accumulated.total_shifts_used || 0,
                    completion_time: accumulated.total_completion_time || 0,
                    solve_time: accumulated.total_completion_time || 0,
                    time_saved_hours: accumulated.total_time_saved_hours || 0,
                    time_saved_minutes: accumulated.total_time_saved_minutes || 0,
                    improvement_percentage: accumulated.total_improvement_percentage || 0,
                    manual_baseline_hours: accumulated.total_manual_baseline_hours || 0,
                    manual_schedule_valid: true,
                    timestamp: accumulated.batches[accumulated.batches.length - 1]?.timestamp || new Date().toISOString()
                  };
                  setLastOptimizationResults(results);
                  setHasOptimized(true);
                  
                  // Fetch fresh details
                  fetchScheduledJobsDetails();
                }
              }
            }
          }
        }
      } catch (error) {
        console.error('❌ [Polling] Failed to fetch optimization status:', error);
      }
    };
    
    // Drive the per-WC progress bar from multi_wc_* WebSocket events.
    // This fires for EVERY work center taking part in the run — a single-WC
    // optimize lights up that one node, and an Optimize-All run lights up a
    // bar beneath every work center it is optimizing (alongside the overall
    // bar in the dispatcher panel).
    // Completion is handled via the existing multiWcResultsReady CustomEvent.
    const handleOptimizationEvent = (event) => {
      try {
        const message = JSON.parse(event.data);
        const payload = message && message.data;
        if (!payload) return;

        const wcs = payload.workCenters;
        const isThisWcInRun = Array.isArray(wcs) && wcs.includes(data.label);
        if (!isThisWcInRun) return;

        if (message.type === 'multi_wc_progress') {
          // New batch starting — arm this WC's per-batch progress bar. In an
          // Optimize-All run no per-WC Optimize button was clicked, so set
          // isOptimizing here; the timer effect needs it to animate the bar.
          setIsOptimizing(true);
          setCurrentBatchNumber(payload.batchNumber || 0);
          setOptimizationStartTime(Date.now());
          setOptimizationProgress(0);
          setPostProcessingStatus(null);
          if (payload.timeLimit) {
            const timeLimitMinutes = Math.max(1, Math.round(payload.timeLimit / 60));
            setOptimizeTimeLimit(timeLimitMinutes);
          }
          if (payload.totalJobsProcessed !== undefined) {
            setActualScheduledJobs(payload.totalJobsProcessed);
          }
        } else if (message.type === 'multi_wc_batch_completed') {
          if (payload.totalJobsOptimized !== undefined) {
            setActualScheduledJobs(payload.totalJobsOptimized);
            setActualOptimizedJobs(payload.totalJobsOptimized);
          }
        } else if (message.type === 'multi_wc_finalizing') {
          setOptimizationProgress(99);
          setPostProcessingStatus('Saving results…');
        }
        // multi_wc_completed / multi_wc_cancelled are handled by the
        // multiWcResultsReady CustomEvent listener above, which clears
        // isOptimizing and triggers the results refresh.
      } catch (err) {
        // Ignore non-JSON messages
      }
    };
    
    // Try to connect to WebSocket using the utility function
    const connectWebSocket = () => {
      try {
        const wsUrl = getWebSocketUrl();
            
        ws = new WebSocket(wsUrl);
        
        ws.onopen = () => {
          console.log('✅ WebSocket connected');
          wsConnected = true;
          
          // Stop polling if it was running
          if (pollingInterval) {
            clearInterval(pollingInterval);
            pollingInterval = null;
            console.log('⏹️ Stopped HTTP polling (WebSocket connected)');
          }
        };
        
        ws.onerror = (error) => {
          console.warn('⚠️ WebSocket error:', error);
          wsConnected = false;
        };
        
        ws.onclose = () => {
          console.log('🔌 WebSocket disconnected');
          wsConnected = false;
          
          // Start HTTP polling as fallback
          if (!pollingInterval) {
            console.log('🔄 Starting HTTP polling fallback (every 3 seconds)');
            pollingInterval = setInterval(pollOptimizationStatus, 3000);
            
            // Also do an immediate poll
            pollOptimizationStatus();
          }
          
          // Try to reconnect after 5 seconds
          if (!reconnectTimeout) {
            reconnectTimeout = setTimeout(() => {
              console.log('🔄 Attempting to reconnect WebSocket...');
              reconnectTimeout = null;
              connectWebSocket();
            }, 5000);
          }
        };
        
        ws.addEventListener('message', handleOptimizationEvent);
      } catch (error) {
        console.error('❌ Failed to create WebSocket:', error);
        wsConnected = false;
        
        // Start polling immediately if WebSocket fails
        if (!pollingInterval) {
          console.log('🔄 Starting HTTP polling fallback (WebSocket failed)');
          pollingInterval = setInterval(pollOptimizationStatus, 3000);
          pollOptimizationStatus();
        }
      }
    };
    
    // Initialize connection
    connectWebSocket();
    
    // Cleanup function
    return () => {
      if (ws) {
        ws.removeEventListener('message', handleOptimizationEvent);
        ws.close();
      }
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
      }
    };
  }, [data.machineId]);

  useEffect(() => {
    if (isEditingDescription && descriptionInputRef.current) {
      descriptionInputRef.current.focus();
      descriptionInputRef.current.select();
    }
  }, [isEditingDescription]);

  useEffect(() => {
    if (isOptimizing && optimizationStartTime) {
      const interval = setInterval(() => {
        const now = Date.now();
        const elapsedSeconds = (now - optimizationStartTime) / 1000;
        const timeLimitSeconds = optimizeTimeLimit * 60; // Convert minutes to seconds
        
        // Calculate progress for current batch
        const realProgress = (elapsedSeconds / timeLimitSeconds) * 100;
        const cappedProgress = Math.min(realProgress, 98);
        
        // Don't override finalizing progress (99%) with timer-based progress
        if (postProcessingStatus) return;
        
        setOptimizationProgress(cappedProgress);
        
        if (elapsedSeconds > timeLimitSeconds * 1.5) {
          console.warn('Current batch taking longer than expected...');
        }
      }, 200);

      return () => clearInterval(interval);
    } else if (!isOptimizing && optimizationProgress > 0) {
      // Reset progress when batch completes
      setOptimizationProgress(0);
    }
  }, [isOptimizing, optimizationStartTime, optimizeTimeLimit, optimizationProgress, postProcessingStatus]);

  useEffect(() => {
    if (!isOptimizing && optimizationProgress > 0) {
      setOptimizationProgress(100);
      setTimeout(() => {
        setOptimizationProgress(0);
        setOptimizationStartTime(null);
        setHasOptimized(true);
      }, 1000);
    }
  }, [isOptimizing, optimizationProgress]);

  // Safety timeout: if stuck in post-processing for >3 minutes, force check
  useEffect(() => {
    if (isOptimizing && postProcessingStatus && optimizationProgress >= 99) {
      const safetyTimeout = setTimeout(async () => {
        console.warn(`⚠️ Machine ${data.machineId} stuck in post-processing for 3 minutes, forcing completion check...`);
        try {
          const apiBaseUrl = process.env.REACT_APP_API_URL || '';
          const response = await fetch(`${apiBaseUrl}/api/machines/${data.machineId}`);
          if (response.ok) {
            const machine = await response.json();
            const accum = machine.specifications?.accumulated_optimization;
            if (accum?.final_files) {
              console.log(`✅ Backend has final files, forcing completion`);
              setIsOptimizing(false);
              setIsAutoOptimizing(false);
              setOptimizationProgress(100);
              setPostProcessingStatus(null);
              setHasOptimized(true);
              fetchScheduledJobsDetails();
            }
          }
        } catch (err) {
          console.error('Failed to check machine status:', err);
        }
      }, 180000); // 3 minutes
      return () => clearTimeout(safetyTimeout);
    }
  }, [isOptimizing, postProcessingStatus, optimizationProgress, data.machineId]);

  useEffect(() => {
    if (data.triggerOptimization) {
      setIsOptimizing(true);
      setOptimizationStartTime(Date.now());
      setOptimizationProgress(0);
    }
  }, [data.triggerOptimization]);

  useEffect(() => {
    const handleGlobalOptimize = (event) => {
      if (event.detail.machineId === data.machineId) {
        const algorithm = event.detail.algorithm || 'ga';
        console.log(`🎯 Machine ${data.machineId} received global optimize trigger with algorithm: ${algorithm}`);
        
        // Store algorithm temporarily for the optimization call
        window.machineOptimizationAlgorithm = algorithm;
        
        handleOptimizeAutoLoop();
      }
    };

    const handleOptimizationComplete = (event) => {
      if (event.detail.machineId === data.machineId) {
        console.log(`✅ Machine ${data.machineId} received completion event`);
      }
    };

    window.addEventListener('trigger-machine-optimize', handleGlobalOptimize);
    window.addEventListener('machine-optimization-complete', handleOptimizationComplete);
    
    return () => {
      window.removeEventListener('trigger-machine-optimize', handleGlobalOptimize);
      window.removeEventListener('machine-optimization-complete', handleOptimizationComplete);
    };
  }, [data.machineId, optimizeTimeLimit]);

  // ============================================================================
  // HANDLERS - Description & Capabilities
  // ============================================================================

  const handleDescriptionSave = () => {
    setIsEditingDescription(false);
    if (description !== (data.description || '')) {
      console.log(`Description updated to: ${description}`);
      if (data.onUpdate) {
        data.onUpdate({ description });
      }
    }
  };

  const handleCapabilitiesSave = () => {
    setIsEditingCapabilities(false);
    if (JSON.stringify(selectedCapabilities.sort()) !== JSON.stringify((data.capabilities || []).sort())) {
      console.log(`Capabilities updated to:`, selectedCapabilities);
      if (data.onUpdate) {
        data.onUpdate({ capabilities: selectedCapabilities });
      }
    }
  };

  const handleDescriptionKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleDescriptionSave();
    } else if (e.key === 'Escape') {
      setDescription(data.description || '');
      setIsEditingDescription(false);
    }
  };

  const handleCapabilityToggle = (capability) => {
    setSelectedCapabilities(prev => {
      if (prev.includes(capability)) {
        return prev.filter(cap => cap !== capability);
      } else {
        return [...prev, capability];
      }
    });
  };

  const handleValueChange = (field, value) => {
    const newValue = (field === 'label' || field === 'type') ? value : Number(value);
    setEditableData(prev => ({ ...prev, [field]: newValue }));
  };

  // ============================================================================
  // HANDLERS - Jobs Modal
  // ============================================================================

  const handleScheduledJobsClick = () => {
    if (scheduledJobsDetails.length > 0) {
      setJobModalMode('optimized');
      setShowJobModal(true);
    }
  };

  const handleAssignedJobsClick = async () => {
    try {
      const apiBaseUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${apiBaseUrl}/api/jobs?machineId=${data.machineId}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch jobs');
      }
      
      const jobs = await response.json();
      setScheduledJobsDetails(jobs);
      setJobModalMode('unoptimized');
      setShowJobModal(true);
    } catch (error) {
      console.error('Failed to fetch jobs:', error);
      alert('Failed to load jobs. Please try again.');
    }
  };

  const closeJobModal = () => {
    setShowJobModal(false);
  };

  // ============================================================================
  // HELPER FUNCTIONS
  // ============================================================================

  /**
   * Get the count of remaining jobs to optimize for this machine (dynamic batch sizing)
   */
  const getRemainingJobCount = async () => {
    try {
      const apiBaseUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${apiBaseUrl}/api/machines/${data.machineId}/remaining-jobs-count`);
      
      if (!response.ok) {
        console.error(`Failed to fetch remaining job count, defaulting to ${batchSize}`);
        return batchSize; // Use configured batch size as fallback
      }
      
      const result = await response.json();
      return result.remainingJobs || 0;
    } catch (error) {
      console.error('Error fetching remaining job count:', error);
      return batchSize; // Use configured batch size as fallback
    }
  };

  // ============================================================================
  // 🆕 SIMPLE OPTIMIZATION TRIGGER - Backend handles the multi-batch loop
  // ============================================================================

  /**
   * Trigger optimization - backend handles all batches automatically
   */
  const handleOptimizeAutoLoop = async () => {
    const startTimestamp = new Date().toLocaleTimeString();
    console.log(`🚀 [${startTimestamp}] USER CLICKED OPTIMIZE - Triggering backend multi-batch optimization for ${data.label}`);
    
    // Check capacity file is uploaded before proceeding
    if (!globalConfig.capacityLoaded) {
      alert('No capacity file uploaded. Please upload a Capacity Template (.xlsx) with per-shift hours for each work center before running the optimization.');
      return;
    }
    
    // 🆕 Get remaining job count for dynamic batch sizing
    const remainingJobs = await getRemainingJobCount();
    console.log(`📊 Remaining jobs to optimize: ${remainingJobs}`);
    
    // 🆕 Calculate dynamic batch size: if less than batchSize+10, use all remaining to avoid tiny final batches
    const threshold = batchSize + 10;
    const dynamicBatchSize = remainingJobs < threshold ? remainingJobs : batchSize;
    console.log(`📊 Batch size for optimization: ${dynamicBatchSize} (configured: ${batchSize}, threshold: ${threshold})`);
    
    // Set optimizing state so the button shows active
    setIsOptimizing(true);
    setIsAutoOptimizing(true);
    setOptimizationError(null);

    // Arm the per-WC progress bar below the Optimize button.
    // The timer-driven effect that animates optimizationProgress requires
    // both isOptimizing and optimizationStartTime to be set.
    setOptimizationStartTime(Date.now());
    setOptimizationProgress(0);
    setPostProcessingStatus(null);

    // Reset state
    setIsPaused(false);
    pauseOptimizationRef.current = false;
    setCurrentBatchNumber(0);
    setCompletedBatches([]);
    setInProgressBatch(null);
    setFileSaveNotification(null);
    setDownloadLinks(null);
    setAllSavedFiles([]);
    
    // Get algorithm from the global trigger, else the method selected in the
    // dispatcher panel (persisted), else default to 'ga'. CP-SAT is retired —
    // never fall back to it even if a stale value is sitting in localStorage.
    const algorithm = window.machineOptimizationAlgorithm
      || (localStorage.getItem('dispatcher-selected-algorithm') !== 'cp-sat'
            && localStorage.getItem('dispatcher-selected-algorithm'))
      || 'ga';
    console.log(`🎯 Using algorithm: ${algorithm}`);
    
    try {
      const apiBaseUrl = process.env.REACT_APP_API_URL || '';
      const apiUrl = `${apiBaseUrl}/api/machines/${data.machineId}/optimize`;
      
      console.log('🌐 Triggering backend optimization:', apiUrl);
      
      console.log(`📅 Current scheduleStartDate state before optimization: "${scheduleStartDate}"`);
      console.log(`📅 Current globalConfig.scheduleStartDate: "${globalConfig.scheduleStartDate}"`);
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          jobCount: dynamicBatchSize, // Use dynamic batch size based on remaining jobs
          timeLimit: optimizeTimeLimit * 60, // Convert minutes to seconds for backend
          algorithm: algorithm,
          multiMode: true,  // Enable multi-batch mode in backend
          // Runtime configuration overrides
          runtimeConfig: {
            scheduleStartDate: scheduleStartDate,
            maxJobs: maxJobs,
            constraints: constraints // Pass constraints configuration
          }
        }),
      });
      
      if (!response.ok) {
        const errorBody = await response.json().catch(() => null);
        throw new Error(errorBody?.error || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('✅ Backend accepted optimization request:', result);

      // The GA runs in the BACKGROUND (no WebSocket progress). The POST returns
      // immediately with { started: true }; we then POLL ga-status until the CSV
      // is ready and surface it as a downloadable result.
      if (algorithm === 'ga') {
        setIsGaRun(true);
        const finishGa = () => {
          setIsOptimizing(false);
          setIsAutoOptimizing(false);
          setIsGaRun(false);
          setPausePending(false);
          gaStopRef.current = null;
        };
        if (!result.started) {
          finishGa();
          setOptimizationError(result.error || 'GA failed to start');
          alert(`GA optimization failed: ${result.error || 'unknown error'}`);
          return;
        }
        const statusUrl = `${apiBaseUrl}/api/machines/${data.machineId}/ga-status`;
        // The backend gaRuns status is the SOURCE OF TRUTH — we poll it until it
        // reports 'done'/'failed' rather than trusting a fixed frontend clock.
        // The hard deadline is only a last-resort guard (matches the backend's 6h
        // process cap). Crucially, each tick re-checks status BEFORE the deadline,
        // so a run that finished while the tab was backgrounded/asleep is still
        // surfaced instead of being discarded as a false timeout (the old bug).
        const hardDeadline = Date.now() + (6 * 60 * 60 + 300) * 1000; // 6h + 5min buffer
        let settled = false;
        let poll = null;

        const applyDone = (s) => {
          const csvUrl = `${apiBaseUrl}${s.download_url || `/api/download/${s.csv_filename}`}`;
          // The Excel belongs next to the CSV here, not only in the dispatcher
          // panel: the dispatcher is told about it through a one-shot window
          // event and keeps it in memory only, so if that panel is not mounted
          // and listening at this moment the link is lost until a reload — and
          // the user is looking at THIS panel anyway.
          const excelUrl = s.excel_filename
            ? `${apiBaseUrl}${s.excel_download_url || `/api/download/${s.excel_filename}`}`
            : null;
          setDownloadLinks({
            csv: csvUrl, csvName: s.csv_filename,
            excel: excelUrl, excelName: s.excel_filename || null,
          });
          setAllSavedFiles([
            { name: s.csv_filename, url: csvUrl, type: 'csv', isFinal: true },
            ...(excelUrl ? [{ name: s.excel_filename, url: excelUrl, type: 'xlsx', isFinal: true }] : []),
          ]);
          setFileSaveNotification(`GA schedule ready: ${s.csv_filename} (${s.jobs_optimized} jobs)${gaWarningsText(s)}`);
          // The results section (and the download list) only renders when these
          // are set — the OR-Tools flow sets them via WebSocket; do it here too.
          setActualScheduledJobs(s.jobs_optimized || 0);
          setLastOptimizationResults({ jobs_optimized: s.jobs_optimized, batches_completed: 1, method: 'ga' });
          setHasOptimized(true);
          // The Excel (all jobs/WCs) belongs in the main dispatcher panel — announce it.
          if (s.excel_filename) {
            window.dispatchEvent(new CustomEvent('gaResultReady', { detail: {
              excelName: s.excel_filename,
              excelUrl: `${apiBaseUrl}${s.excel_download_url || `/api/download/${s.excel_filename}`}`,
              wc: s.work_center, jobs: s.jobs_optimized,
            }}));
          }
        };

        // Fetch status once. Returns true when the run reached a terminal state
        // (done/failed) and was handled; false while still running / unavailable
        // (transient network errors and 'running'/'idle'/'starting' keep polling).
        const checkStatus = async () => {
          let s;
          try {
            s = await fetch(statusUrl).then(r => r.json());
          } catch (e) {
            return false; // transient — keep polling
          }
          if (s.status === 'done' && s.csv_filename) {
            applyDone(s);
            return true;
          }
          if (s.status === 'failed') {
            setOptimizationError(s.error || 'GA optimization failed');
            alert(`GA optimization failed: ${s.error || 'unknown error'}`);
            return true;
          }
          return false;
        };

        const stop = () => {
          if (settled) return;
          settled = true;
          if (poll) clearInterval(poll);
          document.removeEventListener('visibilitychange', onVisible);
          finishGa();
        };
        // Expose the poll-stopper so the Stop button can halt polling immediately
        // (the backend kill is requested separately by handleStopGa).
        gaStopRef.current = stop;

        // Browser timers are throttled/suspended while the tab is hidden or the
        // machine sleeps. When the tab regains focus, re-check immediately so a
        // result that completed while we were away is shown at once.
        const onVisible = () => {
          if (settled || document.visibilityState !== 'visible') return;
          checkStatus().then((terminal) => { if (terminal) stop(); });
        };
        document.addEventListener('visibilitychange', onVisible);

        poll = setInterval(async () => {
          if (settled) return;
          // Tier 1: always re-check status BEFORE giving up — a ready result is
          // never discarded, even past the deadline.
          if (await checkStatus()) { stop(); return; }
          if (Date.now() > hardDeadline) {
            stop();
            setOptimizationError('GA run timed out (no result)');
          }
        }, 4000);
        return;
      }

      // Backend will handle everything and send updates via WebSocket
      // Frontend just needs to listen and display progress

    } catch (error) {
      console.error('❌ Failed to trigger optimization:', error);
      setIsAutoOptimizing(false);
      setIsOptimizing(false);
      alert(`Failed to start optimization: ${error.message}`);
    }
  };

  /**
   * Handle pause button click
   */
  const handlePauseOptimization = async () => {
    const pauseTimestamp = new Date().toLocaleTimeString();
    console.log(`⏸️ [${pauseTimestamp}] USER CLICKED PAUSE`);
    pauseOptimizationRef.current = true;
    
    // Immediate visual feedback - show "pausing..." state
    setPausePending(true);
    
    // Call backend pause endpoint
    try {
      const apiBaseUrl = process.env.REACT_APP_API_URL || '';
      const pauseUrl = `${apiBaseUrl}/api/machines/${data.machineId}/pause`;
      
      console.log(`⏸️ Sending pause request to backend: ${pauseUrl}`);
      const response = await fetch(pauseUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      const result = await response.json();
      console.log(`🔍 Pause response:`, result);
      
      if (result.success) {
        console.log(`✅ Optimization pause requested - will pause after current batch completes`);
        // Don't set isPaused here - wait for WebSocket event
      } else {
        console.log(`⚠️ Pause failed: ${result.error}`);
        setPausePending(false); // Revert if failed
      }
    } catch (error) {
      console.error(`❌ Failed to pause optimization:`, error);
      setPausePending(false); // Revert if failed
    }
  };

  /**
   * Handle Stop button click for a GA run.
   * GA can't be paused/resumed (no checkpointing), so this aborts the run:
   * it asks the backend to kill the python process and stops the local poll
   * loop so the panel returns to the idle Optimize state for a fresh run.
   */
  const handleStopGa = async () => {
    console.log(`⏹️ [${new Date().toLocaleTimeString()}] USER CLICKED STOP GA`);
    setPausePending(true); // reuse for "Stopping..." button feedback
    try {
      const apiBaseUrl = process.env.REACT_APP_API_URL || '';
      const cancelUrl = `${apiBaseUrl}/api/machines/${data.machineId}/ga-cancel`;
      const response = await fetch(cancelUrl, { method: 'POST' });
      const result = await response.json().catch(() => ({}));
      if (!result.success) {
        console.warn(`⚠️ GA cancel response:`, result);
      }
    } catch (error) {
      console.error(`❌ Failed to stop GA run:`, error);
    }
    // Stop the local poll loop and reset the UI regardless of the backend
    // response — the process is being killed; no result will arrive.
    if (gaStopRef.current) gaStopRef.current();
    setOptimizationError('GA run stopped by user');
  };

  /**
   * Handle resume button click
   */
  const handleResumeOptimization = async () => {
    const resumeTimestamp = new Date().toLocaleTimeString();
    console.log(`▶️ [${resumeTimestamp}] USER CLICKED RESUME`);
    
    setIsPaused(false);
    
    try {
      const apiBaseUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${apiBaseUrl}/api/machines/${data.machineId}/resume`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to resume optimization');
      }

      const result = await response.json();
      console.log('✅ Resume successful:', result.message);
      
    } catch (error) {
      console.error('❌ Failed to resume optimization:', error);
      setIsPaused(true); // Revert on failure
      alert(`Failed to resume optimization: ${error.message}`);
    }
  };

  // ============================================================================
  // DATA FETCHING FUNCTIONS
  // ============================================================================

  /**
   * Fetch scheduled jobs details and download links after optimization completes
   */
  const fetchScheduledJobsDetails = async () => {
    try {
      const apiBaseUrl = process.env.REACT_APP_API_URL || '';
      
      // Fetch the machine record to get accumulated_optimization data
      const response = await fetch(`${apiBaseUrl}/api/machines/${data.machineId}`);
      if (!response.ok) {
        throw new Error('Failed to fetch machine data');
      }
      
      const machineData = await response.json();
      console.log('📊 Fetched machine data:', machineData);
      
      if (machineData.accumulated_optimization) {
        const accumulated = machineData.accumulated_optimization;
        
        // Set scheduled jobs details
        if (accumulated.scheduled_jobs && Array.isArray(accumulated.scheduled_jobs)) {
          setScheduledJobsDetails(accumulated.scheduled_jobs);
          console.log(`✅ Set ${accumulated.scheduled_jobs.length} scheduled jobs`);
        }
        
        // Set download links - prefer final files if available, otherwise use accumulated
        const apiBaseUrl = process.env.REACT_APP_API_URL || '';
        const savedFiles = [];
        
        if (accumulated.final_files) {
          // Final combined files are available (all batches completed)
          const finalFiles = accumulated.final_files;
          
          setDownloadLinks({
            txt: finalFiles.txt_filename ? `${apiBaseUrl}/api/download/${finalFiles.txt_filename}` : null,
            csv: `${apiBaseUrl}/api/download/${finalFiles.csv_filename}`,
            excel: finalFiles.excel_filename ? `${apiBaseUrl}/api/download/${finalFiles.excel_filename}` : null,
            txtName: finalFiles.txt_filename || null,
            csvName: finalFiles.csv_filename,
            excelName: finalFiles.excel_filename || null,
            manualTxt: finalFiles.manual_txt_filename ? `${apiBaseUrl}/api/download/${finalFiles.manual_txt_filename}` : null,
            manualCsv: finalFiles.manual_csv_filename ? `${apiBaseUrl}/api/download/${finalFiles.manual_csv_filename}` : null,
            manualTxtName: finalFiles.manual_txt_filename,
            manualCsvName: finalFiles.manual_csv_filename
          });
          
          savedFiles.push(
            { name: finalFiles.csv_filename, url: `${apiBaseUrl}/api/download/${finalFiles.csv_filename}`, type: 'csv', isFinal: true }
          );
          
          if (finalFiles.excel_filename) {
            savedFiles.push({ name: finalFiles.excel_filename, url: `${apiBaseUrl}/api/download/${finalFiles.excel_filename}`, type: 'xlsx', isFinal: true });
          }
          
          if (finalFiles.txt_filename) {
            savedFiles.push({ name: finalFiles.txt_filename, url: `${apiBaseUrl}/api/download/${finalFiles.txt_filename}`, type: 'txt', isFinal: true });
          }
          
          if (finalFiles.manual_csv_filename) {
            savedFiles.push({ name: finalFiles.manual_csv_filename, url: `${apiBaseUrl}/api/download/${finalFiles.manual_csv_filename}`, type: 'csv', isFinal: false });
          }
          if (finalFiles.manual_txt_filename) {
            savedFiles.push({ name: finalFiles.manual_txt_filename, url: `${apiBaseUrl}/api/download/${finalFiles.manual_txt_filename}`, type: 'txt', isFinal: false });
          }
          
          setFileSaveNotification(`Final results saved: ${finalFiles.csv_filename}`);
        } else if (accumulated.accumulated_csv_file) {
          // Accumulated CSV file available (optimization in progress or paused)
          const accumulatedCsvFilename = accumulated.accumulated_csv_file.split('/').pop();
          
          console.log('🔍 Accumulated CSV file path:', accumulated.accumulated_csv_file);
          console.log('🔍 Extracted filename:', accumulatedCsvFilename);
          console.log('🔍 Download URL:', `${apiBaseUrl}/api/download/${accumulatedCsvFilename}`);
          
          setDownloadLinks({
            csv: `${apiBaseUrl}/api/download/${accumulatedCsvFilename}`,
            csvName: accumulatedCsvFilename
          });
          
          savedFiles.push({
            name: accumulatedCsvFilename,
            url: `${apiBaseUrl}/api/download/${accumulatedCsvFilename}`,
            type: 'csv',
            isFinal: false,
            isAccumulated: true
          });
        }
        
        setAllSavedFiles(savedFiles);
        console.log('📥 Set download links:', savedFiles);
      }
    } catch (error) {
      console.error('❌ Failed to fetch scheduled jobs details:', error);
    }
  };

  // ============================================================================
  // UTILITY FUNCTIONS
  // ============================================================================

  const formatTimeForDisplay = (hours) => {
    if (hours < 1) {
      return `${Math.round(hours * 60)}m`;
    }
    return `${hours.toFixed(1)}h`;
  };

  const calculateDuration = (startTime, endTime) => {
    const duration = endTime - startTime;
    return formatTimeForDisplay(duration);
  };

  const getStatusColor = () => {
    return isOptimizing ? '#f39c12' : '#27ae60';
  };

  const getMachineIcon = (type) => {
    switch (type) {
      case 'CNC_MILL': return '🔧';
      case 'ROBOTIC_ARM': return '🤖';
      case 'LASER_CUTTER': return '⚡';
      case '3D_PRINTER': return '🖨️';
      case 'ASSEMBLY_LINE': return '🏭';
      case 'WORK_CENTER': return '⚙️';
      default: return '⚙️';
    }
  };

  const getDisplayedScheduledJobs = () => {
    if (actualScheduledJobs !== null) {
      return actualScheduledJobs;
    }
    return data.scheduledJobs || 0;
  };

  const statusColor = getStatusColor();
  const machineIcon = getMachineIcon(data.type);

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className={`machine-node ${isOptimizing ? 'optimizing' : 'idle'}`}>
      {/* Connection Handles */}
      <Handle
        type="target"
        position={Position.Left}
        style={{ background: '#555' }}
        isConnectable={isConnectable}
        id="jobs-in"
      />

      <Handle
        type="source"
        position={Position.Right}
        style={{ background: statusColor }}
        isConnectable={isConnectable}
        id="status-out"
      />

      {/* Node Header */}
      <div className="node-header machine-header" style={{ borderLeftColor: statusColor }}>
        <div className="node-title" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          {/* Include this work center when the common Optimize All is pressed.
              Checked by default; the choice is remembered per work center. */}
          <input
            type="checkbox"
            checked={optimizeEnabled}
            disabled={isAutoOptimizing}
            onChange={(e) => setWcOptimize(data.label, e.target.checked)}
            onClick={(e) => e.stopPropagation()}
            title={optimizeEnabled
              ? `${data.label} is included in Optimize All — untick to leave it out`
              : `${data.label} is NOT included in Optimize All`}
            style={{
              width: '13px',
              height: '13px',
              cursor: isAutoOptimizing ? 'not-allowed' : 'pointer',
              accentColor: '#27ae60',
              margin: 0,
              flexShrink: 0
            }}
          />
          <span className="machine-icon">{machineIcon}</span>
          <span style={{ fontSize: '14px', fontWeight: 'bold' }}>
            Work Center {editableData.label}
          </span>
        </div>
        <div className="node-actions" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {/* Settings Text Button */}
          <button
            onClick={() => setShowAdvancedConfig(!showAdvancedConfig)}
            disabled={isAutoOptimizing}
            title="Click to configure optimization settings"
            style={{
              background: 'none',
              border: 'none',
              cursor: isAutoOptimizing ? 'not-allowed' : 'pointer',
              padding: '4px 8px',
              fontSize: '11px',
              fontWeight: '600',
              color: showAdvancedConfig ? '#667eea' : '#7f8c8d',
              opacity: isAutoOptimizing ? 0.5 : 1,
              transition: 'all 0.2s',
              borderRadius: '4px'
            }}
            onMouseEnter={(e) => {
              if (!isAutoOptimizing) {
                e.currentTarget.style.color = '#667eea';
                e.currentTarget.style.backgroundColor = 'rgba(102, 126, 234, 0.1)';
              }
            }}
            onMouseLeave={(e) => {
              if (!showAdvancedConfig) {
                e.currentTarget.style.color = '#7f8c8d';
              }
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            Settings
          </button>

          {/* Constraints Text Button */}
          <button
            onClick={() => setShowConstraintsPanel(true)}
            disabled={isAutoOptimizing || constraints === null}
            title="Click to configure constraints"
            style={{
              background: 'none',
              border: 'none',
              cursor: (isAutoOptimizing || constraints === null) ? 'not-allowed' : 'pointer',
              padding: '4px 8px',
              fontSize: '11px',
              fontWeight: '600',
              color: showConstraintsPanel ? '#667eea' : '#7f8c8d',
              opacity: (isAutoOptimizing || constraints === null) ? 0.5 : 1,
              transition: 'all 0.2s',
              borderRadius: '4px'
            }}
            onMouseEnter={(e) => {
              if (!isAutoOptimizing && constraints !== null) {
                e.currentTarget.style.color = '#667eea';
                e.currentTarget.style.backgroundColor = 'rgba(102, 126, 234, 0.1)';
              }
            }}
            onMouseLeave={(e) => {
              if (!showConstraintsPanel) {
                e.currentTarget.style.color = '#7f8c8d';
              }
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            Constraints
          </button>
          
          <div className="machine-status-indicator">
            <span 
              className={`status-dot ${isOptimizing ? 'pulsing' : ''}`}
              style={{ backgroundColor: statusColor }}
            ></span>
          </div>
        </div>
      </div>

      {/* Node Body */}
      <div className="node-body machine-body" style={{
        textAlign: 'left',
        fontSize: '12px'
      }}>
        {/* Description Section */}
        <div className="machine-description">
          {isEditingDescription ? (
            <input
              ref={descriptionInputRef}
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onBlur={handleDescriptionSave}
              onKeyDown={handleDescriptionKeyPress}
              className="description-input"
              placeholder="Enter machine description..."
              style={{
                width: '100%',
                padding: '6px 12px',
                fontSize: '12px',
                border: '1px solid rgba(102, 126, 234, 0.3)',
                borderRadius: '6px',
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                outline: 'none',
                transition: 'all 0.2s'
              }}
            />
          ) : (
            <div 
              onClick={() => setIsEditingDescription(true)}
              style={{
                padding: '6px 12px',
                fontSize: '12px',
                color: description ? '#2c3e50' : '#95a5a6',
                backgroundColor: 'rgba(102, 126, 234, 0.05)',
                borderRadius: '6px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                border: '1px solid transparent'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = 'rgba(102, 126, 234, 0.1)';
                e.target.style.borderColor = 'rgba(102, 126, 234, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'rgba(102, 126, 234, 0.05)';
                e.target.style.borderColor = 'transparent';
              }}
            >
              {description || 'Click to add description...'}
            </div>
          )}
        </div>

        {/* Capabilities Section */}
        <div style={{ 
          marginTop: '12px', 
          padding: '8px 0',
          borderTop: '1px solid rgba(0,0,0,0.08)'
        }}>
          <div style={{ 
            fontSize: '11px', 
            fontWeight: 'bold', 
            color: '#667eea',
            marginBottom: '6px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span>Capabilities:</span>
            <button
              onClick={() => setIsEditingCapabilities(!isEditingCapabilities)}
              style={{
                padding: '2px 8px',
                fontSize: '10px',
                background: isEditingCapabilities ? '#667eea' : 'transparent',
                color: isEditingCapabilities ? 'white' : '#667eea',
                border: '1px solid #667eea',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: '600',
                transition: 'all 0.2s'
              }}
            >
              {isEditingCapabilities ? 'Done' : 'Edit'}
            </button>
          </div>

          {isEditingCapabilities ? (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '4px',
              marginTop: '6px'
            }}>
              {availableCapabilities.map(cap => (
                <label
                  key={cap}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    fontSize: '10px',
                    padding: '4px 6px',
                    backgroundColor: selectedCapabilities.includes(cap) 
                      ? 'rgba(102, 126, 234, 0.1)' 
                      : 'rgba(0,0,0,0.02)',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                    border: '1px solid',
                    borderColor: selectedCapabilities.includes(cap)
                      ? 'rgba(102, 126, 234, 0.3)'
                      : 'transparent'
                  }}
                  onMouseEnter={(e) => {
                    if (!selectedCapabilities.includes(cap)) {
                      e.currentTarget.style.backgroundColor = 'rgba(102, 126, 234, 0.05)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!selectedCapabilities.includes(cap)) {
                      e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.02)';
                    }
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedCapabilities.includes(cap)}
                    onChange={() => handleCapabilityToggle(cap)}
                    style={{ margin: 0 }}
                  />
                  <span style={{ 
                    fontWeight: selectedCapabilities.includes(cap) ? '600' : '400',
                    color: selectedCapabilities.includes(cap) ? '#667eea' : '#666'
                  }}>
                    {cap.replace(/_/g, ' ')}
                  </span>
                </label>
              ))}
            </div>
          ) : (
            <div style={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: '4px',
              marginTop: '4px'
            }}>
              {selectedCapabilities.length > 0 ? (
                selectedCapabilities.map(cap => (
                  <span
                    key={cap}
                    style={{
                      padding: '3px 8px',
                      fontSize: '10px',
                      backgroundColor: 'rgba(102, 126, 234, 0.1)',
                      color: '#667eea',
                      borderRadius: '12px',
                      fontWeight: '500',
                      border: '1px solid rgba(102, 126, 234, 0.2)'
                    }}
                  >
                    {cap.replace(/_/g, ' ')}
                  </span>
                ))
              ) : (
                <span style={{ 
                  fontSize: '10px', 
                  color: '#95a5a6',
                  fontStyle: 'italic'
                }}>
                  No capabilities selected
                </span>
              )}
            </div>
          )}
        </div>

        {/* Metrics Section */}
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: '8px',
          marginTop: '12px',
          padding: '10px',
          backgroundColor: 'rgba(0,0,0,0.02)',
          borderRadius: '8px'
        }}>
          {/* Assigned Jobs */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            padding: '6px 8px',
            backgroundColor: 'white',
            borderRadius: '6px',
            border: '1px solid rgba(0,0,0,0.06)'
          }}>
            <span style={{ fontWeight: '600', color: '#2c3e50', fontSize: '11px' }}>
              📋 Assigned Jobs:
            </span>
            <span 
              onClick={handleAssignedJobsClick}
              style={{ 
                fontWeight: 'bold', 
                color: '#667eea',
                fontSize: '13px',
                cursor: 'pointer',
                padding: '2px 8px',
                borderRadius: '4px',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = 'rgba(102, 126, 234, 0.1)';
                e.target.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.transform = 'scale(1)';
              }}
            >
              {data.assignedJobs || 0}
            </span>
          </div>

          {/* Scheduled/Optimized Jobs */}
          {actualScheduledJobs !== null && actualScheduledJobs >= 0 && (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              padding: '6px 8px',
              backgroundColor: 'white',
              borderRadius: '6px',
              border: '1px solid rgba(0,0,0,0.06)'
            }}>
              <span style={{ fontWeight: '600', color: '#2c3e50', fontSize: '11px' }}>
                ✅ Optimized Jobs:
              </span>
              <span 
                onClick={handleScheduledJobsClick}
                style={{ 
                  fontWeight: 'bold', 
                  color: actualScheduledJobs > 0 ? '#27ae60' : '#e74c3c',
                  fontSize: '13px',
                  cursor: actualScheduledJobs > 0 ? 'pointer' : 'default',
                  padding: '2px 8px',
                  borderRadius: '4px',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  if (actualScheduledJobs > 0) {
                    e.target.style.backgroundColor = 'rgba(39, 174, 96, 0.1)';
                    e.target.style.transform = 'scale(1.05)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.transform = 'scale(1)';
                }}
              >
                {actualScheduledJobs}
              </span>
            </div>
          )}

          {/* 🆕 Batch Progress Indicator — only show when multi-batch (batch > 1) */}
          {isAutoOptimizing && currentBatchNumber > 1 && (
            <div style={{
              padding: '8px',
              backgroundColor: 'rgba(243, 156, 18, 0.1)',
              borderRadius: '6px',
              border: '1px solid rgba(243, 156, 18, 0.3)',
              fontSize: '11px',
              color: '#f39c12',
              fontWeight: '600'
            }}>
              🔄 Processing Batch #{currentBatchNumber}
            </div>
          )}
        </div>

        {/* Optimization Controls */}
        <div style={{ 
          marginTop: '12px',
          padding: '10px',
          backgroundColor: 'rgba(102, 126, 234, 0.05)',
          borderRadius: '8px',
          border: '1px solid rgba(102, 126, 234, 0.1)'
        }}>
          {/* Current Batch Header */}
          <div style={{
            fontSize: '11px',
            fontWeight: 'bold',
            color: '#667eea',
            marginBottom: '10px',
            paddingBottom: '6px',
            borderBottom: '1px solid rgba(102, 126, 234, 0.2)'
          }}>
            Current Batch
          </div>
          
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '8px'
          }}>
            <label style={{ 
              fontSize: '11px', 
              fontWeight: '600',
              color: '#667eea',
              minWidth: '90px'
            }}>
              Time Limit per Batch:
            </label>
            <input
              type="number"
              value={optimizeTimeLimit}
              onChange={(e) => {
                const newTimeLimit = Number(e.target.value);
                setOptimizeTimeLimit(newTimeLimit);
                // Save to localStorage for persistence (store in minutes)
                localStorage.setItem(`wc-${data.label}-timeLimit`, newTimeLimit.toString());
                console.log(`⏱️ Saved time limit to localStorage: ${newTimeLimit} min`);
              }}
              min="1"
              max="60"
              step="1"
              disabled={isAutoOptimizing}
              style={{
                flex: 1,
                padding: '4px 8px',
                fontSize: '11px',
                border: '1px solid rgba(102, 126, 234, 0.3)',
                borderRadius: '4px',
                backgroundColor: 'white',
                fontWeight: '500'
              }}
            />
            <span style={{ fontSize: '11px', color: '#7f8c8d' }}>min</span>
          </div>

          {/* Advanced Configuration Section - Collapsible */}
          {showAdvancedConfig && (
            <div style={{
              backgroundColor: 'rgba(102, 126, 234, 0.03)',
              borderRadius: '6px',
              padding: '10px',
              marginBottom: '8px',
              border: '1px solid rgba(102, 126, 234, 0.15)'
            }}>
              <div style={{
                fontSize: '10px',
                fontWeight: 'bold',
                color: '#667eea',
                marginBottom: '8px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Advanced Settings
              </div>

              {/* Batch Size Configuration */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '8px'
              }}>
                <label style={{ 
                  fontSize: '11px', 
                  fontWeight: '600',
                  color: '#667eea',
                  minWidth: '90px'
                }}>
                  Batch Size:
                </label>
                <input
                  type="number"
                  value={batchSize}
                  onChange={(e) => {
                    const newBatchSize = Number(e.target.value);
                    setBatchSize(newBatchSize);
                    setMaxJobs(newBatchSize);
                    localStorage.setItem(`wc-${data.label}-batchSize`, newBatchSize.toString());
                  }}
                  min="1"
                  max="200"
                  step="1"
                  disabled={isAutoOptimizing}
                  style={{
                    flex: 1,
                    padding: '4px 8px',
                    fontSize: '11px',
                    border: '1px solid rgba(102, 126, 234, 0.3)',
                    borderRadius: '4px',
                    backgroundColor: 'white',
                    fontWeight: '500'
                  }}
                />
                <span style={{ fontSize: '11px', color: '#7f8c8d' }}>jobs</span>
              </div>

              {/* Schedule Start Date */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '0'
              }}>
                <label style={{ 
                  fontSize: '11px', 
                  fontWeight: '600',
                  color: '#667eea',
                  minWidth: '90px'
                }}>
                  Start Date:
                </label>
                <input
                  type="text"
                  value={scheduleStartDate}
                  onChange={(e) => {
                    setScheduleStartDate(e.target.value);
                    localStorage.setItem(`wc-${data.label}-scheduleStartDate`, e.target.value);
                  }}
                  placeholder="DD/MM/YYYY HH:MM"
                  disabled={isAutoOptimizing}
                  style={{
                    flex: 1,
                    padding: '4px 8px',
                    fontSize: '11px',
                    border: '1px solid rgba(102, 126, 234, 0.3)',
                    borderRadius: '4px',
                    backgroundColor: 'white',
                    fontWeight: '500'
                  }}
                />
              </div>
            </div>
          )}

          <div style={{
            fontSize: '10px',
            color: '#95a5a6',
            fontStyle: 'italic',
            marginBottom: '8px'
          }}>
            Batch size: {batchSize} jobs
          </div>

          {/* Optimize / Pause / Resume Button */}
          {!isAutoOptimizing && !isPaused ? (
            <button
              onClick={() => handleOptimizeAutoLoop()}
              disabled={isOptimizing || isGlobalOptimizing}
              title={isGlobalOptimizing ? 'Optimize All is running — wait for it to finish' : ''}
              style={{
                width: '100%',
                padding: '10px',
                fontSize: '13px',
                fontWeight: 'bold',
                color: 'white',
                backgroundColor: (isOptimizing || isGlobalOptimizing) ? '#95a5a6' : '#667eea',
                border: 'none',
                borderRadius: '6px',
                cursor: (isOptimizing || isGlobalOptimizing) ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s',
                boxShadow: (isOptimizing || isGlobalOptimizing) ? 'none' : '0 2px 4px rgba(102, 126, 234, 0.3)'
              }}
              onMouseEnter={(e) => {
                if (!isOptimizing && !isGlobalOptimizing) {
                  e.target.style.backgroundColor = '#5a67d8';
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = '0 4px 8px rgba(102, 126, 234, 0.4)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isOptimizing && !isGlobalOptimizing) {
                  e.target.style.backgroundColor = '#667eea';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 2px 4px rgba(102, 126, 234, 0.3)';
                }
              }}
            >
              {isGlobalOptimizing ? '🔒 Optimize All Running...' : isOptimizing ? '⏳ Optimizing...' : '🚀 Optimize'}
            </button>
          ) : isPaused ? (
            <button
              onClick={handleResumeOptimization}
              style={{
                width: '100%',
                padding: '10px',
                fontSize: '13px',
                fontWeight: 'bold',
                color: 'white',
                backgroundColor: '#27ae60',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                boxShadow: '0 2px 4px rgba(39, 174, 96, 0.3)'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#229954';
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = '0 4px 8px rgba(39, 174, 96, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#27ae60';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 2px 4px rgba(39, 174, 96, 0.3)';
              }}
            >
              ▶️ Resume Optimization
            </button>
          ) : (
            <button
              onClick={isGaRun ? handleStopGa : handlePauseOptimization}
              disabled={pausePending}
              style={{
                width: '100%',
                padding: '10px',
                fontSize: '13px',
                fontWeight: 'bold',
                color: 'white',
                backgroundColor: pausePending ? '#95a5a6' : (isGaRun ? '#e74c3c' : '#f39c12'),
                border: 'none',
                borderRadius: '6px',
                cursor: pausePending ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s',
                boxShadow: pausePending ? 'none' : (isGaRun ? '0 2px 4px rgba(231, 76, 60, 0.3)' : '0 2px 4px rgba(243, 156, 18, 0.3)'),
                opacity: pausePending ? 0.7 : 1
              }}
              onMouseEnter={(e) => {
                if (!pausePending) {
                  e.target.style.backgroundColor = isGaRun ? '#c0392b' : '#e67e22';
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = isGaRun ? '0 4px 8px rgba(231, 76, 60, 0.4)' : '0 4px 8px rgba(243, 156, 18, 0.4)';
                }
              }}
              onMouseLeave={(e) => {
                if (!pausePending) {
                  e.target.style.backgroundColor = isGaRun ? '#e74c3c' : '#f39c12';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = isGaRun ? '0 2px 4px rgba(231, 76, 60, 0.3)' : '0 2px 4px rgba(243, 156, 18, 0.3)';
                }
              }}
            >
              {isGaRun
                ? (pausePending ? '⏹️ Stopping GA run...' : '⏹️ Stop GA Run')
                : (pausePending ? '⏸️ Pausing after batch completes...' : '⏸️ Pause Optimization')}
            </button>
          )}

          {/* Pause Pending Message - REMOVED, now shown on button */}

          {/* Progress Bar with Percentage Display */}
          {optimizationProgress > 0 && optimizationProgress < 100 && (
            <div style={{ marginTop: '10px' }}>
              <div style={{
                fontSize: '10px',
                color: postProcessingStatus ? '#f39c12' : '#667eea',
                marginBottom: '4px',
                fontWeight: '500'
              }}>
                {postProcessingStatus 
                  ? `💾 ${postProcessingStatus}`
                  : `Batch Progress: ${Math.round(optimizationProgress)}%`
                }
              </div>
              <div style={{ 
                height: '6px',
                backgroundColor: 'rgba(0,0,0,0.1)',
                borderRadius: '3px',
                overflow: 'hidden'
              }}>
                <div style={{
                  height: '100%',
                  width: `${optimizationProgress}%`,
                  backgroundColor: postProcessingStatus ? '#f39c12' : '#667eea',
                  transition: 'width 0.3s ease',
                  borderRadius: '3px'
                }} />
              </div>
              {/* Status text below the progress bar */}
              {!postProcessingStatus && optimizationProgress >= 98 && (
                <div style={{ fontSize: '9px', color: '#95a5a6', marginTop: '3px', textAlign: 'center', fontStyle: 'italic' }}>
                  ⏳ Solver finishing up…
                </div>
              )}
            </div>
          )}

          {/* Optimization Error Display */}
          {optimizationError && (
            <div style={{
              marginTop: '8px',
              padding: '6px 8px',
              backgroundColor: '#fff5f5',
              border: '1px solid #e74c3c',
              borderRadius: '6px',
              fontSize: '10px',
              color: '#c0392b'
            }}>
              ❌ {optimizationError}
              <span
                style={{ marginLeft: '6px', cursor: 'pointer', textDecoration: 'underline', opacity: 0.7 }}
                onClick={() => setOptimizationError(null)}
              >
                dismiss
              </span>
            </div>
          )}

          {/* Status Messages */}
          {hasOptimized && lastOptimizationResults && (
            <>
              {actualScheduledJobs > 0 ? (
                <div style={{ 
                  marginTop: '12px',
                  padding: '10px',
                  backgroundColor: 'rgba(39, 174, 96, 0.1)',
                  borderRadius: '6px',
                  border: '1px solid rgba(39, 174, 96, 0.3)',
                  fontSize: '11px',
                  color: '#27ae60'
                }}>
                  <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                    ✅ Optimization Successful
                  </div>
                  <div>
                    📦 Completed {lastOptimizationResults.batches_completed || 1} batch{lastOptimizationResults.batches_completed > 1 ? 'es' : ''}
                  </div>
                  <div>
                    ⏱️ Using {lastOptimizationResults.shifts_used} shift{lastOptimizationResults.shifts_used !== 1 ? 's' : ''}, total time: {lastOptimizationResults.completion_time?.toFixed(1)}h
                  </div>
                  
                  {/* âœ… Fallback Schedule Indicator */}
                  {lastOptimizationResults.fallback_schedule && (
                    <div style={{ 
                      fontSize: '10px', 
                      color: '#f39c12', 
                      marginTop: '6px',
                      paddingTop: '6px',
                      borderTop: '1px solid rgba(0,0,0,0.1)',
                      fontWeight: '600'
                    }}>
                      âš ï¸ Used fallback schedule (optimizer couldn't find solution)
                    </div>
                  )}
                  
                  {lastOptimizationResults.cancelled && (
                    <div style={{ marginTop: '6px', paddingTop: '6px', borderTop: '1px solid rgba(0,0,0,0.1)', color: '#f39c12', fontWeight: '600' }}>
                      ⚠️ Optimization cancelled by user
                    </div>
                  )}
                  
                  {fileSaveNotification && (
                    <div style={{ marginTop: '6px', paddingTop: '6px', borderTop: '1px solid rgba(0,0,0,0.1)' }}>
                      📁 {fileSaveNotification}
                    </div>
                  )}
                  
                  {/* 📥 All Saved Files List */}
                  {allSavedFiles && allSavedFiles.length > 0 && (
                    <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid rgba(0,0,0,0.08)' }}>
                      <div style={{ fontWeight: 'bold', marginBottom: '6px', fontSize: '10px', color: '#7f8c8d' }}>
                        📥 Results File
                      </div>
                      <div style={{ maxHeight: '120px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        {allSavedFiles.map((f, idx) => (
                          <div key={idx} style={{ display: 'flex', gap: '6px', alignItems: 'center', fontSize: '9px' }}>
                            <div style={{ 
                              flex: 1, 
                              color: f.isFinal ? '#27ae60' : f.isAccumulated ? '#e67e22' : '#34495e',
                              fontWeight: f.isFinal || f.isAccumulated ? 'bold' : 'normal',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              {f.isFinal && '⭐ '}{f.isAccumulated && '📊 '}{f.name}
                            </div>
                            <button 
                              onClick={() => downloadFile(f.url, f.name)} 
                              style={{ 
                                padding: '2px 6px', 
                                fontSize: '9px', 
                                borderRadius: '3px', 
                                border: 'none', 
                                background: f.isFinal ? '#27ae60' : f.isAccumulated ? '#e67e22' : '#667eea', 
                                color: '#fff', 
                                cursor: 'pointer',
                                fontWeight: '600'
                              }}
                              onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                              onMouseLeave={(e) => e.target.style.opacity = '1'}
                            >
                              {f.type.toUpperCase()}
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ 
                  marginTop: '12px',
                  padding: '10px',
                  backgroundColor: 'rgba(231, 76, 60, 0.1)',
                  borderRadius: '6px',
                  border: '1px solid rgba(231, 76, 60, 0.3)',
                  fontSize: '11px',
                  color: '#e74c3c'
                }}>
                  <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                    ⚠️ Optimization Result: 0 Jobs Scheduled
                  </div>
                  {data.assignedJobs === 0 ? (
                    <div style={{ color: '#7f8c8d' }}>
                      ℹ️ No jobs are currently assigned to this work center
                    </div>
                  ) : (
                    <>
                      <div style={{ color: '#7f8c8d', marginBottom: '4px' }}>
                        ℹ️ Optimization completed but no feasible schedule was found
                      </div>
                      <div style={{ fontSize: '10px', color: '#95a5a6', fontStyle: 'italic' }}>
                        Possible reasons: constraints too tight, time limit too short, or jobs have conflicting requirements
                      </div>
                      {lastOptimizationResults.solve_time && (
                        <div style={{ marginTop: '6px', paddingTop: '6px', borderTop: '1px solid rgba(0,0,0,0.1)' }}>
                          ⏱️ Attempted for {lastOptimizationResults.solve_time?.toFixed(1)}s
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Multi-WC CSV Download (shown after Optimize All completes) */}
        {multiWcCsvFile && multiWcJobCount > 0 && (
          <div style={{
            marginTop: '10px',
            padding: '8px 10px',
            background: 'rgba(39, 174, 96, 0.1)',
            border: '1px solid rgba(39, 174, 96, 0.3)',
            borderRadius: '6px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span style={{ fontSize: '11px', color: '#27ae60', fontWeight: 'bold' }}>
              ✅ {multiWcJobCount} jobs optimized
            </span>
            <button
              onClick={() => downloadFile(`/api/download/${multiWcCsvFile}`, multiWcCsvFile)}
              style={{
                padding: '4px 10px',
                background: '#27ae60',
                border: 'none',
                borderRadius: '4px',
                color: 'white',
                fontSize: '11px',
                fontWeight: 'bold',
                cursor: 'pointer',
                transition: 'opacity 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.opacity = '0.8'}
              onMouseLeave={(e) => e.target.style.opacity = '1'}
            >
              📥 CSV
            </button>
          </div>
        )}
      </div>

      {/* Job Details Modal */}
      <JobsTableModal
        isOpen={showJobModal}
        onClose={closeJobModal}
        jobs={scheduledJobsDetails}
        title={jobModalMode === 'optimized' 
          ? `Optimized Job Sequence - ${editableData.label}` 
          : `Assigned Jobs - ${editableData.label}`}
        mode={jobModalMode}
        downloadLinks={downloadLinks}
        optimizationResults={lastOptimizationResults}
      />

      {/* Constraints Configuration Panel */}
      {showConstraintsPanel && constraints !== null && (
        <ConstraintsPanel
          constraints={constraints}
          workCenterName={editableData.label}
          onClose={() => setShowConstraintsPanel(false)}
          onApply={async (updatedConstraints) => {
            setConstraints(updatedConstraints);
            console.log('✅ Constraints updated:', updatedConstraints);
            
            // Save to backend
            try {
              const apiBaseUrl = process.env.REACT_APP_API_URL || '';
              const response = await fetch(`${apiBaseUrl}/api/config/constraints/${data.label}`, {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({ constraints: updatedConstraints })
              });
              
              const result = await response.json();
              
              if (result.success) {
                console.log('💾 Constraints saved to backend successfully');
                alert('✅ Constraints saved successfully!');
              } else {
                console.error('Failed to save constraints:', result.error);
                alert(`❌ Failed to save constraints: ${result.error}`);
              }
            } catch (error) {
              console.error('Error saving constraints:', error);
              alert(`❌ Error saving constraints: ${error.message}`);
            }
          }}
          disabled={isAutoOptimizing}
        />
      )}
    </div>
  );
};

export default memo(MachineNode);